export const NAV_LINKS = [
    { label: "Home", href: "/" },
    { label: "How It Works", href: "/how-it-works" },
    {
        label: "Platform",
        href: "/product",
        children: [
            { label: "Building Sectors", href: "/sectors" },
            { label: "BuildEase Pro", href: "/pro" },
            { label: "Plan Intelligence", href: "/product#exploration" },
            { label: "BOQ & Cost Intelligence", href: "/sample-report" },
            { label: "Quote Analysis", href: "/product#quote-analyzer" },
            { label: "Home Vision & 3D", href: "/home-vision" },
            { label: "Materials Intelligence", href: "/materials-intelligence" },
            { label: "Green & Energy Intelligence", href: "/product#ecosystem" },
        ],
    },
    { label: "Pricing", href: "/pricing" },
    {
        label: "Resources",
        href: "/insights",
        children: [
            { label: "BuildEase Insights", href: "/insights" },
            { label: "FAQs", href: "/faqs" },
            { label: "Sample Report", href: "/sample-report" },
            { label: "Trust & Transparency", href: "/trust" },
        ],
    },
    { label: "Contact", href: "/#contact" },
    { label: "About", href: "/about" },
];

export const CTA = { label: "Get Started", href: "/join-the-pilot" };

// Default BuildEase business location. Location hierarchy everywhere in the
// platform: 1) user-approved project address or coordinates, 2) user-selected
// project city or region, 3) Durban as the fallback when no project location
// has been provided. Never overwrite a genuine project or user location.
export const DEFAULT_LOCATION = {
    city: "Durban",
    province: "KwaZulu-Natal",
    country: "South Africa",
    lat: -29.8587,
    lng: 31.0218,
};

export const LOCATION_COORDINATE_META = `${Math.abs(DEFAULT_LOCATION.lat).toFixed(4)}° S / ${Math.abs(DEFAULT_LOCATION.lng).toFixed(4)}° E`;

export const HOME_CHALLENGES = [
    {
        code: "C.01",
        title: "COST UNCERTAINTY",
        copy: "How much will your home actually cost? Without structured quantities and current pricing, budgets are guesswork.",
    },
    {
        code: "C.02",
        title: "COMPLICATED INFORMATION",
        copy: "Architectural plans, specifications and schedules are full of detail most homeowners were never taught to read.",
    },
    {
        code: "C.03",
        title: "EVALUATING QUOTES",
        copy: "Contractor quotations differ in scope, specifications and exclusions — comparing them line by line is hard work.",
    },
    {
        code: "C.04",
        title: "NAVIGATING REQUIREMENTS",
        copy: "Compliance considerations, documentation and professional responsibilities sit behind every residential build.",
    },
];

export const HOME_CAPABILITIES = [
    {
        id: "plan-intelligence",
        title: "Plan Intelligence",
        copy: "Understand your architectural plans — rooms, levels, dimensions and the information they contain. For example, confirm whether your drawn areas and room layout match what you expected.",
        status: "IN THE PILOT",
        image:
            "https://images.unsplash.com/photo-1542621334-a254cf47733d?q=80&w=1600&auto=format&fit=crop",
        alt: "Architectural floor plan being drawn with a scale ruler",
        href: "/product#exploration",
    },
    {
        id: "boq-cost",
        title: "BOQ & Cost Intelligence",
        copy: "Structured quantities and cost estimates, with assumptions stated and verified information labelled. For example, see what your foundations and walls contribute to the total before you price them.",
        status: "IN THE PILOT",
        image:
            "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1600&auto=format&fit=crop",
        alt: "Financial documents and a calculator being reviewed",
        href: "/sample-report",
    },
    {
        id: "quote-analysis",
        title: "Quote Analysis",
        copy: "Examine contractor quotations line by line — scope, pricing differences and items requiring clarification. For example, spot when one quote excludes site preparation that another includes.",
        status: "IN THE PILOT",
        image:
            "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1600&auto=format&fit=crop",
        alt: "Documents being compared and reviewed at a desk",
        href: "/product#quote-analyzer",
    },
    {
        id: "materials",
        title: "Materials Intelligence",
        copy: "Material pricing sourced at your location, with sources, dates and units shown — verified before displayed. For example, compare brick prices from suppliers near your site, with dates shown.",
        status: "IN THE PILOT",
        image:
            "https://images.unsplash.com/photo-1595414440701-da000c40df9c?q=80&w=1600&auto=format&fit=crop",
        alt: "Construction material palette of brick, timber and steel",
        href: "/materials-intelligence",
    },
    {
        id: "home-vision",
        title: "Home Vision & 3D",
        copy: "Explore visual concepts of your home — exterior, interior and finishes — before construction begins. For example, compare a slate roof against concrete tiles on your own home before you choose.",
        status: "IN THE PILOT",
        image:
            "https://images.unsplash.com/photo-1657346088167-b982455bf29a?q=80&w=1600&auto=format&fit=crop",
        alt: "Contemporary double-storey home with large glazing",
        href: "/home-vision",
    },
    {
        id: "compliance",
        title: "Compliance Intelligence",
        copy: "Understand the building requirements and documentation behind your project, made clearer over time. For example, learn what NHBRC registration involves before your bank asks for it.",
        status: "PLANNED",
        image:
            "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?q=80&w=1600&auto=format&fit=crop",
        alt: "Contemporary institutional building facade with angled metal fins",
        href: "/faqs",
    },
    {
        id: "green-energy",
        title: "Green & Energy Intelligence",
        copy: "Energy-conscious design, renewable options and sustainability considerations for your planned home. For example, understand what your roof orientation could mean for future solar savings.",
        status: "PLANNED",
        image:
            "https://images.unsplash.com/photo-1595437193398-f24279553f4f?q=80&w=1600&auto=format&fit=crop",
        alt: "Solar panels installed on a residential roof in sunlight",
        href: "/product#ecosystem",
    },
];

export const HOME_STEPS = [
    {
        n: "01",
        title: "Start Your Project",
        copy: "Create your project and tell us about the home you intend to build.",
    },
    {
        n: "02",
        title: "Upload Your Plans",
        copy: "Submit the architectural plans and documents you already have.",
    },
    {
        n: "03",
        title: "Analyse Your Project",
        copy: "BuildEase interprets the available information and organises what matters.",
    },
    {
        n: "04",
        title: "Explore Your Insights",
        copy: "Review quantities, cost estimates, quotation observations and visual concepts.",
    },
    {
        n: "05",
        title: "Make Informed Decisions",
        copy: "Compare options, ask better questions and move forward with clarity.",
    },
];

export const HOME_COMMITMENTS = [
    {
        title: "EVIDENCE & INFORMATION",
        copy: "Extracted, calculated, estimated and assumed information is labelled — never blended.",
    },
    {
        title: "COST TRANSPARENCY",
        copy: "Material pricing shows its source, date, unit and geographic relevance.",
    },
    {
        title: "UNCERTAINTY & LIMITATIONS",
        copy: "Missing information and unresolved items are surfaced, not hidden.",
    },
    {
        title: "RESPONSIBLE AI",
        copy: "AI supports construction insights — and is always labelled as fallible.",
    },
    {
        title: "PROFESSIONAL REVIEW",
        copy: "BuildEase supports decisions; it does not replace qualified professionals.",
    },
];

export const STAGES = [
    "PLAN",
    "UNDERSTAND",
    "VISUALISE",
    "PRICE",
    "VERIFY",
    "BUILD",
    "MANAGE",
];

export const scrollToSection = (selector) => {
    const lenis = window.__lenis;
    if (lenis) {
        lenis.scrollTo(selector, { duration: 1.4 });
        return;
    }
    const el = document.querySelector(selector);
    if (el) el.scrollIntoView({ behavior: "smooth" });
};
